import { IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import AutocompleteControl from '@/components/Form/AutocompleteControl';
import { mapWhitelabelToControlOption } from '@/helpers/Whitelabels';
import { getFieldErrorProps } from '@/helpers/form';
import { useAppSelector } from '@/store/hooks';
import { selectClientsControlOptions } from '@/store/selectors/clients.selectors';
import { selectRibeyeAllWhitelabels } from '@/store/selectors/sharedApi.selectors';
import { Button, InputLabel, TextField } from '@mui/material';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

interface IProps {
  openAdvertiserModal: () => void;
  openAgencyModal: () => void;
}

const ClientInfo = ({ openAdvertiserModal, openAgencyModal }: IProps) => {
  const { control } = useFormContext();

  const whitelabels = useAppSelector(selectRibeyeAllWhitelabels);
  const { advertisers, agencies } = useAppSelector(selectClientsControlOptions);

  const whitelabelControlOptions = useMemo(() => (whitelabels ?? []).map(mapWhitelabelToControlOption), [whitelabels]);

  const selectedWhitelabel: IWhitelabelControlOption | null = useWatch({ control, name: 'whitelabel' });

  return (
    <>
      <div>
        <InputLabel htmlFor='name'>
          Name<span className='text-[#fe4033]'>*</span>
        </InputLabel>
        <Controller
          name='name'
          control={control}
          rules={{
            required: {
              value: true,
              message: 'Please enter name',
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              placeholder='Enter Client Name'
              id='name'
              {...getFieldErrorProps(fieldState)}
            />
          )}
        />
      </div>
      <div className='mt-4'>
        <InputLabel htmlFor='whitelabel'>
          Whitelabel<span className='text-[#fe4033]'>*</span>
        </InputLabel>
        <AutocompleteControl
          name='whitelabel'
          required
          control={control}
          options={whitelabelControlOptions}
          id='whitelabel-select'
          placeholder='Select whitelabel'
        />
      </div>
      <div className='mt-4'>
        <InputLabel htmlFor='agency'>Agencies</InputLabel>
        <AutocompleteControl
          name='agencies'
          id='agencies-select'
          options={agencies}
          control={control}
          placeholder='Select agencies'
          disabled={!selectedWhitelabel}
          multiple
        />
        <Button
          sx={{ height: 40, width: 200, marginTop: 2 }}
          color='primary'
          variant='contained'
          disabled={!selectedWhitelabel}
          onClick={openAgencyModal}
        >
          + Create new Agency
        </Button>
      </div>
      <div className='mt-4'>
        <InputLabel htmlFor='advertisers'>Advertisers</InputLabel>
        <AutocompleteControl
          name='advertisers'
          id='advertisers-select'
          options={advertisers}
          control={control}
          placeholder='Select advertisers'
          disabled={!selectedWhitelabel}
          multiple
        />
        <Button
          sx={{ height: 40, width: 200, marginTop: 2 }}
          color='primary'
          variant='contained'
          disabled={!selectedWhitelabel}
          onClick={openAdvertiserModal}
        >
          + Create new Advertiser
        </Button>
      </div>
      <div className='mt-4'>
        <InputLabel htmlFor='externalId'>External ID</InputLabel>
        <Controller
          name='externalId'
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              placeholder='Enter External ID'
              id='externalId'
              {...getFieldErrorProps(fieldState)}
            />
          )}
        />
      </div>
    </>
  );
};

export default ClientInfo;
